import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Buffer added to the sticky header height. Once a section's top has
// scrolled under the header + this buffer, it is considered "in reading".
const READ_LINE_BUFFER_PX = 40;

// When the user clicks a nav anchor, we briefly override the active
// state to the click target, so the indicator does not "dance" through
// intermediate sections during a long smooth scroll.
const LOCK_DELAY_MS = 180; // wait so the eye starts following first
const LOCK_RELEASE_MS = 900; // typical smooth-scroll window

function getHeaderHeight() {
    if (typeof document === "undefined") return 0;
    // Measure the inner sticky bar (logo + nav), excluding any open
    // mobile dropdown so the threshold stays stable while the menu opens.
    const bar = document.querySelector('[data-testid="site-header-bar"]');
    if (bar) return bar.getBoundingClientRect().height;
    const header = document.querySelector('[data-testid="site-header"]');
    return header ? header.getBoundingClientRect().height : 0;
}

/**
 * Tracks which section is currently being read.
 *
 * Scroll logic: iterate through the section ids in DOM order; the last
 * one whose top has passed the "reading line" (sticky header height +
 * a small buffer) wins. Threshold recomputes on resize.
 *
 * Click logic: when the user clicks a nav anchor pointing to one of the
 * tracked sections, the indicator is locked to the destination after a
 * short delay and released once the smooth scroll has settled. This
 * keeps the indicator stable instead of jumping across intermediate
 * sections during a long scroll.
 *
 * Returns the matching `#hash` (e.g. "#situations") or "" if none.
 */
export function useActiveSection(hashes) {
    const [active, setActive] = useState("");
    const [locked, setLocked] = useState(null);
    const location = useLocation();

    useEffect(() => {
        const ids = hashes.map((h) => h.replace("#", ""));

        const compute = () => {
            const threshold = getHeaderHeight() + READ_LINE_BUFFER_PX;
            let current = "";
            for (const id of ids) {
                const el = document.getElementById(id);
                if (!el) continue;
                if (el.getBoundingClientRect().top <= threshold) {
                    current = `#${id}`;
                }
            }
            setActive(current);
        };

        compute();
        window.addEventListener("scroll", compute, { passive: true });
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("scroll", compute);
            window.removeEventListener("resize", compute);
        };
    }, [hashes, location.pathname]);

    useEffect(() => {
        let lockTimer = null;
        let releaseTimer = null;

        const onAnchorClick = (e) => {
            const link = e.target.closest("a[href]");
            if (!link) return;
            const href = link.getAttribute("href") || "";
            // Accept both "#section" (same-route) and "/#section" (cross-route)
            const hashIndex = href.indexOf("#");
            if (hashIndex < 0) return;
            const hash = href.slice(hashIndex);
            if (!hashes.includes(hash)) return;

            clearTimeout(lockTimer);
            clearTimeout(releaseTimer);
            lockTimer = setTimeout(() => setLocked(hash), LOCK_DELAY_MS);
            releaseTimer = setTimeout(() => setLocked(null), LOCK_RELEASE_MS);
        };

        document.addEventListener("click", onAnchorClick);
        return () => {
            document.removeEventListener("click", onAnchorClick);
            clearTimeout(lockTimer);
            clearTimeout(releaseTimer);
        };
    }, [hashes]);

    // Deep-link entry: when arriving on a URL with a hash (e.g. /#situations),
    // lock the indicator to the destination immediately so it doesn't flicker
    // through intermediate sections while the browser auto-scrolls.
    useEffect(() => {
        if (!location.hash || !hashes.includes(location.hash)) return undefined;
        setLocked(location.hash);
        const t = setTimeout(() => setLocked(null), LOCK_RELEASE_MS);
        return () => clearTimeout(t);
    }, [location.hash, hashes]);

    return locked || active;
}
